import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCRM } from '../../contexts/CRMContext';
import crmApi from '../../utils/crmApi';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckIcon, 
  SparklesIcon,
  ClockIcon,
  CreditCardIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const SubscriptionPage = () => {
  const { planCatalog, subscription, loadSubscriptionData } = useSubscription();
  const { currentAgency } = useCRM();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('month');
  const [couponCode, setCouponCode] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, [currentAgency]);

  const loadSubscriptionStatus = async () => {
    try {
      const res = await crmApi.get('/subscription/status');
      if (res.data?.success) {
        setSubscriptionStatus(res.data.subscription);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!planId || planId === 'trial') {
      toast.error('Please select a valid plan');
      return;
    }

    try {
      setLoading(true);
      const res = await crmApi.post('/subscription/checkout', {
        planId,
        billingPeriod,
        couponCode: couponCode || undefined
      });

      if (res.data?.success && res.data.url) {
        // Redirect to Stripe checkout
        window.location.href = res.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.message || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  // Filter out trial and enterprise - enterprise requires B2B agreement
  const plans = planCatalog ? Object.entries(planCatalog)
    .filter(([key]) => key !== 'trial' && key !== 'enterprise')
    .map(([key, plan]) => ({ id: key, ...plan })) : [];
  
  // Get Enterprise plan separately for display (but not for selection)
  const enterprisePlan = planCatalog?.enterprise ? { id: 'enterprise', ...planCatalog.enterprise } : null;

  const currentPlanId = subscription?.tier?.toLowerCase() || 'trial';
  const isTrialActive = subscriptionStatus?.isTrialActive;
  const trialDaysRemaining = subscriptionStatus?.trialDaysRemaining;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-foreground">Choose Your Plan</h1>
        <p className="text-muted-foreground">
          Select the perfect plan for your agency. All plans include a 14-day free trial.
        </p>
      </div>

      {/* Trial Status Banner */}
      {isTrialActive && trialDaysRemaining !== null && (
        <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClockIcon className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  Free Trial Active
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-blue-100 text-blue-800">
              {trialDaysRemaining} days left
            </Badge>
          </div>
        </Card>
      )}

      {/* Billing Period Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg border border-border bg-muted p-1">
          <button
            onClick={() => setBillingPeriod('month')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingPeriod === 'month'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('year')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingPeriod === 'year'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly <span className="text-green-600 ml-1">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const price = billingPeriod === 'year' 
            ? (plan.priceYearly || plan.price * 10) 
            : plan.price;
          const isCurrentPlan = currentPlanId === plan.id;
          const isPopular = plan.id === 'professional';

          return (
            <Card
              key={plan.id}
              className={`relative p-6 space-y-4 ${
                isPopular ? 'border-2 border-primary shadow-lg' : ''
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    Most Popular
                  </Badge>
                </div>
              )}

              <div>
                <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-foreground">€{price}</span>
                  <span className="text-muted-foreground">
                    /{billingPeriod === 'year' ? 'year' : 'month'}
                  </span>
                </div>
                {billingPeriod === 'year' && plan.priceYearly && (
                  <p className="text-sm text-muted-foreground mt-1">
                    €{plan.price}/{billingPeriod === 'year' ? 'year' : 'month'} billed annually
                  </p>
                )}
              </div>

              {isCurrentPlan && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Current Plan
                </Badge>
              )}

              <ul className="space-y-2">
                {Object.entries(plan.features || {})
                  .filter(([key, value]) => typeof value === 'boolean' && value)
                  .slice(0, 8)
                  .map(([key]) => (
                    <li key={key} className="flex items-center gap-2 text-sm">
                      <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </li>
                  ))}
                {plan.features?.maxSeats && (
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {plan.features.maxSeats === -1 
                        ? 'Unlimited' 
                        : `${plan.features.maxSeats}`} team members
                    </span>
                  </li>
                )}
                {plan.features?.maxClients && (
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {plan.features.maxClients === -1 
                        ? 'Unlimited' 
                        : `${plan.features.maxClients}`} clients
                    </span>
                  </li>
                )}
              </ul>

              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading || isCurrentPlan}
                className="w-full"
                variant={isPopular ? 'default' : 'outline'}
              >
                {isCurrentPlan ? (
                  'Current Plan'
                ) : loading && selectedPlan === plan.id ? (
                  'Processing...'
                ) : (
                  <>
                    <CreditCardIcon className="h-4 w-4 mr-2" />
                    {isTrialActive ? 'Subscribe Now' : 'Get Started'}
                  </>
                )}
              </Button>
            </Card>
          );
        })}
        
        {/* Enterprise Plan - Contact Sales Only */}
        {enterprisePlan && (
          <Card className="relative p-6 space-y-4 border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 dark:from-purple-950/20 to-blue-50 dark:to-blue-950/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-purple-600 text-white">
                Enterprise
              </Badge>
            </div>

            <div>
              <h3 className="text-xl font-bold text-foreground">{enterprisePlan.name}</h3>
              <div className="mt-2">
                <span className="text-3xl font-bold text-foreground">Custom</span>
                <span className="text-muted-foreground ml-2">Pricing</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                B2B agreement required
              </p>
            </div>

            {currentPlanId === 'enterprise' && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Current Plan
              </Badge>
            )}

            <ul className="space-y-2">
              {Object.entries(enterprisePlan.features || {})
                .filter(([key, value]) => typeof value === 'boolean' && value)
                .slice(0, 8)
                .map(([key]) => (
                  <li key={key} className="flex items-center gap-2 text-sm">
                    <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-muted-foreground capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </li>
                ))}
              <li className="flex items-center gap-2 text-sm">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-muted-foreground">Unlimited team members</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-muted-foreground">Unlimited clients</span>
              </li>
            </ul>

            <Button
              onClick={() => navigate('/contact', { state: { from: window.location.pathname } })}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              variant="default"
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              Contact Sales
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              Enterprise plans require a B2B agreement. Contact our sales team for custom pricing and features.
            </p>
          </Card>
        )}
      </div>


      {/* Features Comparison */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-foreground mb-4">Feature Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2 font-semibold">Feature</th>
                {plans.map((plan) => (
                  <th key={plan.id} className="text-center p-2 font-semibold">
                    {plan.name}
                  </th>
                ))}
                {enterprisePlan && (
                  <th key="enterprise" className="text-center p-2 font-semibold">
                    {enterprisePlan.name}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {[
                'maxSeats',
                'maxClients',
                'maxAccounts',
                'aiCopilot',
                'advancedAnalytics',
                'emailCampaigns',
                'clientPortal',
                'whiteLabel',
                'advancedIntegrations'
              ].map((feature) => (
                <tr key={feature} className="border-b border-border">
                  <td className="p-2 text-muted-foreground capitalize">
                    {feature.replace(/([A-Z])/g, ' $1').trim()}
                  </td>
                  {plans.map((plan) => {
                    const value = plan.features?.[feature];
                    return (
                      <td key={plan.id} className="p-2 text-center">
                        {typeof value === 'boolean' ? (
                          value ? (
                            <CheckIcon className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : value === -1 ? (
                          <span className="text-green-600 font-semibold">Unlimited</span>
                        ) : (
                          <span>{value || '—'}</span>
                        )}
                      </td>
                    );
                  })}
                  {enterprisePlan && (() => {
                    const value = enterprisePlan.features?.[feature];
                    return (
                      <td key="enterprise" className="p-2 text-center">
                        {typeof value === 'boolean' ? (
                          value ? (
                            <CheckIcon className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : value === -1 ? (
                          <span className="text-green-600 font-semibold">Unlimited</span>
                        ) : (
                          <span>{value || '—'}</span>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionPage;

